// Bilingual content for the Stratex-AI homepage.
// Ported verbatim from the design handoff (src/copy.js).

export type Transcript = { who: "ai" | "caller"; text: string };
export type Industry = {
  tag: string;
  title: string;
  body: string;
  bullets: string[];
  image?: string;
};
export type Product = { num: string; name: string; title: string; body: string };
export type Automation = { tag: string; title: string; body: string };
export type RoiItem = { v: number; pre?: string; suf?: string; l: string; sub: string };
export type Quote = { text: string; who: string; role: string };
export type PriceTier = {
  name: string;
  price: string;
  per: string;
  desc: string;
  feats: string[];
  cta: string;
  featured?: boolean;
};
export type FooterCol = { title: string; items: string[] };

export type Copy = {
  nav: {
    products: string;
    industries: string;
    integrations: string;
    pricing: string;
    cases: string;
    cta: string;
  };
  hero: {
    eyebrow: string;
    title_a: string;
    title_b: string;
    title_c: string;
    sub: string;
    cta: string;
    cta2: string;
    note: string;
  };
  logos: { title: string };
  industries: { eyebrow: string; title_a: string; title_b: string; items: Industry[] };
  products: {
    eyebrow: string;
    title_a: string;
    title_b: string;
    sub: string;
    items: Product[];
  };
  fonio: {
    eyebrow: string;
    title_a: string;
    title_b: string;
    sub: string;
    bullets: string[];
    transcript: Transcript[];
    stats: { v: string; l: string }[];
  };
  automations: {
    eyebrow: string;
    title_a: string;
    title_b: string;
    sub: string;
    items: Automation[];
  };
  integrations: {
    eyebrow: string;
    title_a: string;
    title_b: string;
    sub: string;
    note: string;
  };
  roi: { eyebrow: string; title_a: string; title_b: string; items: RoiItem[] };
  cases: { eyebrow: string; title_a: string; title_b: string; quotes: Quote[] };
  pricing: {
    eyebrow: string;
    title_a: string;
    title_b: string;
    sub: string;
    tiers: PriceTier[];
  };
  finalcta: {
    eyebrow: string;
    title_a: string;
    title_b: string;
    title_c: string;
    sub: string;
    cta: string;
    cta2: string;
  };
  footer: { tagline: string; cols: FooterCol[]; copy: string };
};

export type Lang = "de" | "en";

export const COPY: Record<Lang, Copy> = {
  de: {
    nav: {
      products: "Produkte",
      industries: "Branchen",
      integrations: "Integrationen",
      pricing: "Preise",
      cases: "Kunden",
      cta: "Gespräch vereinbaren",
    },
    hero: {
      eyebrow: "SaaS & Automatisierung für Hotellerie, Property & Gastro",
      title_a: "Weniger Admin.",
      title_b: "Mehr",
      title_c: "Gastfreundschaft.",
      sub: "Stratex-AI verbindet Ihr PMS, Ihre Telefonanlage und Ihre Backoffice-Tools zu einer einzigen intelligenten Ebene – damit Ihr Team sich auf Gäste konzentriert, nicht auf Systeme.",
      cta: "Gespräch vereinbaren",
      cta2: "Produkt ansehen",
      note: "Deutsch geführtes Onboarding · DSGVO-konform · Hosting in der EU",
    },
    logos: {
      title: "Integriert mit den PMS, die Ihr Team bereits nutzt",
    },
    industries: {
      eyebrow: "Branchen",
      title_a: "Gebaut für Häuser,",
      title_b: "in denen der Gast im Mittelpunkt steht.",
      items: [
        {
          tag: "Hotellerie",
          title: "Vom Boutique-Hotel bis zur Kette",
          body: "Automatisierte Rezeption, intelligenter Voice-Agent für Anrufe außerhalb der Öffnungszeiten, nahtlose PMS-Synchronisation von Reservation bis Rechnung.",
          bullets: [
            "Automatische Bestätigungen & Upsell",
            "KI-Telefonie 24/7",
            "Housekeeping-Koordination",
          ],
          image: "/assets/hotel.webp",
        },
        {
          tag: "Service Apartments",
          title: "Long-Stay, ohne Aufwand",
          body: "Buchungen aus mehreren Plattformen synchronisiert, digitaler Check-in mit Smart Lock und automatisches Reinigungs-Routing — von 7 Tagen bis 6 Monaten Aufenthalt.",
          bullets: [
            "Mobile Check-in & Smart Locks",
            "Multi-Channel Buchungssync",
            "Reinigungs- & Wäsche-Disposition",
          ],
          image: "/assets/service-apartments.webp",
        },
        {
          tag: "Co-living & Micro-Living",
          title: "Community-Stays mit System",
          body: "Mitgliederverwaltung, Gemeinschaftsräume und wiederkehrende Abrechnung in einer Plattform — vom 30-Tage-Stay bis zur Jahres­mitgliedschaft.",
          bullets: [
            "Digitales Mitglieder-Onboarding",
            "Common-Space Reservierung",
            "Recurring Billing & Verträge",
          ],
          image: "/assets/co-living.webp",
        },
        {
          tag: "Gastronomie",
          title: "Restaurants & F&B Operations",
          body: "Reservierungen, Rückfragen und No-Show-Management laufen über eine Schicht – sprachgesteuert, mehrsprachig, immer erreichbar.",
          bullets: [
            "Tisch-Reservierung per Anruf",
            "Allergie- & Sonderwünsche",
            "Integration mit Kassensystem",
          ],
          image: "/assets/gastronomie.webp",
        },
      ],
    },
    products: {
      eyebrow: "Plattform",
      title_a: "Ein Betriebssystem",
      title_b: "für Ihr Gästeerlebnis.",
      sub: "Vier Module. Eine API-Ebene. Live-Daten aus Ihrem PMS, Ihrer Telefonanlage und Ihrem Posteingang.",
      items: [
        {
          num: "01",
          name: "Core",
          title: "Unified Guest Layer",
          body: "Ein zentrales Gästeprofil über alle Kanäle – Telefon, E-Mail, Web, PMS.",
        },
        {
          num: "02",
          name: "Voice",
          title: "KOVA AI Telefonie",
          body: "KI-Voice-Agent nimmt Anrufe entgegen, qualifiziert, bucht und übergibt bei Bedarf.",
        },
        {
          num: "03",
          name: "Flows",
          title: "Automation Studio",
          body: "Visueller Editor für Workflows – von der Buchungsbestätigung bis zum Late-Check-out.",
        },
        {
          num: "04",
          name: "Insights",
          title: "Operations Analytics",
          body: "Messen Sie Gesprächsqualität, Antwortzeiten und Upsell-Quote in Echtzeit.",
        },
      ],
    },
    fonio: {
      eyebrow: "KOVA AI · Voice",
      title_a: "Der Voice-Agent,",
      title_b: "der wirklich zuhört.",
      sub: "KOVA AI beantwortet Anrufe in natürlicher Sprache – mehrsprachig, kontextbewusst, an Ihr PMS angebunden. Kein IVR. Kein „Drücken Sie die 1“.",
      bullets: [
        "Mehrsprachig: 35+ Sprachen",
        "Ultrarealistisch in Deutsch, Schweizer Deutsch und Österreichisch",
        "Erkennt Anliegen, bucht Tische, prüft Verfügbarkeit",
        "Übergibt fließend an Mitarbeitende",
        "Zusammenfassung landet im CRM/PMS",
      ],
      transcript: [
        { who: "ai", text: "Guten Tag, Hotel Alpenblick, was kann ich für Sie tun?" },
        { who: "caller", text: "Ich hätte gern ein Zimmer vom 12. bis 14. Mai, Doppel." },
        {
          who: "ai",
          text: "Einen Moment, ich prüfe die Verfügbarkeit im PMS … Ein Superior-Doppelzimmer wäre frei, 189 € pro Nacht inkl. Frühstück.",
        },
        { who: "caller", text: "Ja, das passt. Und gibt es einen Parkplatz?" },
        {
          who: "ai",
          text: "Ja, Tiefgarage 18 € pro Nacht. Ich reserviere einen Stellplatz und schicke Ihnen die Bestätigung per E-Mail.",
        },
      ],
      stats: [
        { v: "94%", l: "Anrufe ohne Übergabe gelöst" },
        { v: "2,1s", l: "Durchschnittliche Antwortzeit" },
        { v: "24/7", l: "Erreichbarkeit" },
      ],
    },
    automations: {
      eyebrow: "Automatisierungen",
      title_a: "Prozesse,",
      title_b: "die sich selbst erledigen.",
      sub: "Sofort einsatzbereite Flows – und ein Editor für alles darüber hinaus.",
      items: [
        {
          tag: "Pre-Arrival",
          title: "Digitaler Check-in 48h vorher",
          body: "Gast erhält Link, füllt Meldeschein aus, wählt Ankunftszeit, bucht Upsells.",
        },
        {
          tag: "In-House",
          title: "Late Check-out Angebot",
          body: "Um 9:00 prüft der Flow freie Zimmer und bietet berechtigten Gästen Late-Check-out an.",
        },
        {
          tag: "Post-Stay",
          title: "Review-Routing",
          body: "Gute Bewertungen gehen zu Google, kritische zuerst in Ihren Posteingang.",
        },
        {
          tag: "Property Mgmt",
          title: "Schaden-Triage",
          body: "Voice-Agent qualifiziert Anruf, erstellt Ticket, plant Handwerker.",
        },
        {
          tag: "F&B",
          title: "No-Show Prevention",
          body: "Automatische Bestätigungs-SMS 3h vor Reservierung, bei Absage wird der Tisch freigegeben.",
        },
        {
          tag: "Finance",
          title: "Rechnungs-Automation",
          body: "PMS-Rechnung → DATEV, mit Mehrwertsteuer-Split Beherbergung/Frühstück.",
        },
      ],
    },
    integrations: {
      eyebrow: "PMS & Tools",
      title_a: "Funktioniert mit Ihrem",
      title_b: "bestehenden Stack.",
      sub: "Native Integrationen mit den PMS- und Ops-Tools, die Ihr Haus bereits nutzt. Keine Migration nötig.",
      note: "Sie nutzen etwas, das hier fehlt? Wir bauen die Integration in der Regel in unter 14 Tagen.",
    },
    roi: {
      eyebrow: "Kennzahlen",
      title_a: "Was unsere Häuser",
      title_b: "in 90 Tagen messen.",
      items: [
        {
          v: 62,
          suf: "%",
          l: "weniger verpasste Anrufe",
          sub: "gemessen über alle Voice-Kunden im 1. Quartal",
        },
        {
          v: 18,
          pre: "+",
          suf: "h",
          l: "Zeit gewonnen pro Woche",
          sub: "pro Front-Office-Mitarbeiter:in",
        },
        {
          v: 31,
          pre: "+",
          suf: "%",
          l: "Upsell-Quote",
          sub: "bei aktiviertem Pre-Arrival Flow",
        },
        { v: 14, l: "Tage bis Go-Live", sub: "bei Standard-PMS-Integration" },
      ],
    },
    cases: {
      eyebrow: "Kundenstimmen",
      title_a: "Vertraut von Häusern",
      title_b: "in D-A-CH und darüber hinaus.",
      quotes: [
        {
          text: "Seit Stratex-AI verpassen wir keinen Anruf mehr nach 18 Uhr. Die Rezeption hat wieder Zeit für die Gäste vor Ort.",
          who: "Lea Hartmann",
          role: "GM, Hotel Alpenblick · 142 Zimmer",
        },
        {
          text: "Die Protel-Schnittstellen haben mit den anderen Systemen fehlerfrei harmoniert.",
          who: "Marco Bühler",
          role: "Direktor, Grauhotel",
        },
        {
          text: "Für unsere 2.400 Einheiten ist der Voice-Agent die erste Linie.",
          who: "Jana Weiss",
          role: "COO, Verwaltung Süd",
        },
      ],
    },
    pricing: {
      eyebrow: "Preise",
      title_a: "Transparent.",
      title_b: "Pro Haus.",
      sub: "Keine Setup-Gebühren in den ersten 30 Tagen. Jährliche Abrechnung möglich.",
      tiers: [
        {
          name: "Starter",
          price: "€ 490",
          per: "/ Monat / Haus",
          desc: "Einstieg für einzelne Häuser – PMS-Integration, Core-Modul, Standard-Flows.",
          feats: ["1 PMS-Integration", "Core & Flows", "E-Mail-Support"],
          cta: "Gespräch vereinbaren",
        },
        {
          name: "Growth",
          price: "€ 1.290",
          per: "/ Monat / Haus",
          desc: "Für Häuser, die Voice brauchen. Inkl. KOVA AI mit 1.500 Minuten.",
          feats: [
            "Alles aus Starter",
            "KOVA AI · 1.500 Min.",
            "Automation Studio",
            "Prioritäts-Support",
          ],
          cta: "Gespräch vereinbaren",
          featured: true,
        },
        {
          name: "Enterprise",
          price: "Individuell",
          per: "Ketten & Portfolios",
          desc: "Multi-Property, SSO, individuelle Integrationen, SLA.",
          feats: [
            "Unbegrenzte Häuser",
            "SSO & Rollenmodell",
            "Custom Integrationen",
            "Dedicated CSM",
          ],
          cta: "Anfrage stellen",
        },
      ],
    },
    finalcta: {
      eyebrow: "Jetzt starten",
      title_a: "30 Minuten.",
      title_b: "Kein Pitch.",
      title_c: "Nur Ihr Stack.",
      sub: "Wir sehen uns Ihr aktuelles Setup an, zeigen zwei, drei Automatisierungen und sagen ehrlich, ob Stratex-AI für Sie passt.",
      cta: "Gespräch vereinbaren",
      cta2: "demo@stratex-ai.com",
    },
    footer: {
      tagline: "SaaS & Automatisierung für Gastgewerbe.",
      cols: [
        { title: "Produkt", items: ["Core", "KOVA AI", "Automation Studio", "Insights"] },
        { title: "Branchen", items: ["Hotellerie", "Gebäudeverwaltung", "Gastronomie"] },
        { title: "Unternehmen", items: ["Über uns", "Kunden", "Karriere", "Blog"] },
        { title: "Rechtliches", items: ["Impressum", "Datenschutz", "AGB", "DPA"] },
      ],
      copy: "© 2026 Stratex-AI GmbH · Zürich · Wien · München",
    },
  },

  en: {
    nav: {
      products: "Product",
      industries: "Industries",
      integrations: "Integrations",
      pricing: "Pricing",
      cases: "Customers",
      cta: "Book a call",
    },
    hero: {
      eyebrow: "SaaS & automation for hospitality, property & F&B",
      title_a: "Less admin.",
      title_b: "More",
      title_c: "hospitality.",
      sub: "Stratex-AI connects your PMS, your phone system and your back-office tools into a single intelligent layer — so your team focuses on guests, not on software.",
      cta: "Book a call",
      cta2: "See the product",
      note: "German-led onboarding · GDPR compliant · EU hosting",
    },
    logos: {
      title: "Native integrations with the PMS your team already uses",
    },
    industries: {
      eyebrow: "Industries",
      title_a: "Built for properties",
      title_b: "where the guest comes first.",
      items: [
        {
          tag: "Hospitality",
          title: "From boutique to chain",
          body: "Automated reception, a smart voice agent for after-hours calls, seamless PMS sync from reservation to invoice.",
          bullets: [
            "Automated confirmations & upsell",
            "AI telephony 24/7",
            "Housekeeping coordination",
          ],
          image: "/assets/hotel.webp",
        },
        {
          tag: "Service Apartments",
          title: "Long-stay, fully automated",
          body: "Bookings synced across multiple platforms, digital check-in with smart locks and auto-routed cleaning — for 7-day to 6-month stays.",
          bullets: [
            "Mobile check-in & smart locks",
            "Multi-channel booking sync",
            "Cleaning & laundry routing",
          ],
          image: "/assets/service-apartments.webp",
        },
        {
          tag: "Co-living & micro-living",
          title: "Community stays, structured",
          body: "Member management, common-space bookings and recurring billing on one platform — from 30-day stays to annual memberships.",
          bullets: [
            "Digital member onboarding",
            "Common-space reservations",
            "Recurring billing & contracts",
          ],
          image: "/assets/co-living.webp",
        },
        {
          tag: "F&B",
          title: "Restaurants & F&B operations",
          body: "Reservations, questions and no-show management run through one layer — voice-driven, multilingual, always available.",
          bullets: [
            "Table booking by phone",
            "Allergies & special requests",
            "POS integration",
          ],
          image: "/assets/gastronomie.webp",
        },
      ],
    },
    products: {
      eyebrow: "Platform",
      title_a: "One operating system",
      title_b: "for your guest experience.",
      sub: "Four modules. One API layer. Live data from your PMS, phone system and inbox.",
      items: [
        {
          num: "01",
          name: "Core",
          title: "Unified guest layer",
          body: "A single guest profile across all channels — phone, email, web, PMS.",
        },
        {
          num: "02",
          name: "Voice",
          title: "KOVA AI telephony",
          body: "AI voice agent takes calls, qualifies, books and hands off when needed.",
        },
        {
          num: "03",
          name: "Flows",
          title: "Automation Studio",
          body: "Visual editor for workflows — from booking confirmations to late check-out.",
        },
        {
          num: "04",
          name: "Insights",
          title: "Operations analytics",
          body: "Measure call quality, response times and upsell rate in real time.",
        },
      ],
    },
    fonio: {
      eyebrow: "KOVA AI · Voice",
      title_a: "The voice agent",
      title_b: "that actually listens.",
      sub: "KOVA AI answers calls in natural language — multilingual, context-aware, wired into your PMS. No IVR. No “press 1”.",
      bullets: [
        "Multilingual: 35+ languages",
        "Ultra-realistic in German, Swiss German and Austrian German",
        "Understands intent, books tables, checks availability",
        "Fluent hand-off to staff",
        "Call summary lands in your CRM/PMS",
      ],
      transcript: [
        { who: "ai", text: "Good afternoon, Hotel Alpenblick, how can I help?" },
        { who: "caller", text: "I'd like a room from May 12 to 14, double." },
        {
          who: "ai",
          text: "One moment, checking availability in the PMS … a superior double is available at €189/night including breakfast.",
        },
        { who: "caller", text: "Great. Is parking available?" },
        {
          who: "ai",
          text: "Yes, underground at €18/night. I'll reserve a spot and email you the confirmation.",
        },
      ],
      stats: [
        { v: "94%", l: "Calls resolved without hand-off" },
        { v: "2.1s", l: "Average response time" },
        { v: "24/7", l: "Availability" },
      ],
    },
    automations: {
      eyebrow: "Automations",
      title_a: "Processes",
      title_b: "that run themselves.",
      sub: "Ready-made flows — and an editor for everything beyond.",
      items: [
        {
          tag: "Pre-arrival",
          title: "Digital check-in 48h before",
          body: "Guest gets a link, fills in registration, picks arrival time, books upsells.",
        },
        {
          tag: "In-house",
          title: "Late check-out offer",
          body: "At 9am the flow checks free rooms and offers late check-out to eligible guests.",
        },
        {
          tag: "Post-stay",
          title: "Review routing",
          body: "Good reviews go to Google, critical ones to your inbox first.",
        },
        {
          tag: "Property Mgmt",
          title: "Damage triage",
          body: "Voice agent qualifies the call, creates a ticket, schedules a contractor.",
        },
        {
          tag: "F&B",
          title: "No-show prevention",
          body: "Auto-SMS 3h before reservation, table is released on cancel.",
        },
        {
          tag: "Finance",
          title: "Invoice automation",
          body: "PMS invoice → DATEV, with VAT split lodging/breakfast.",
        },
      ],
    },
    integrations: {
      eyebrow: "PMS & tools",
      title_a: "Works with your",
      title_b: "existing stack.",
      sub: "Native integrations with the PMS and ops tools your property already runs. No migration required.",
      note: "Using something not listed here? We typically ship an integration in under 14 days.",
    },
    roi: {
      eyebrow: "Metrics",
      title_a: "What our properties",
      title_b: "measure within 90 days.",
      items: [
        {
          v: 62,
          suf: "%",
          l: "fewer missed calls",
          sub: "across all Voice customers in Q1",
        },
        {
          v: 18,
          pre: "+",
          suf: "h",
          l: "time saved per week",
          sub: "per front-office team member",
        },
        {
          v: 31,
          pre: "+",
          suf: "%",
          l: "upsell rate",
          sub: "with Pre-arrival flow active",
        },
        { v: 14, l: "days to go-live", sub: "for standard PMS integrations" },
      ],
    },
    cases: {
      eyebrow: "Customer voices",
      title_a: "Trusted by properties",
      title_b: "across D-A-CH and beyond.",
      quotes: [
        {
          text: "Since Stratex-AI we don't miss a single call after 6pm. Reception is back to caring for the guests in front of them.",
          who: "Lea Hartmann",
          role: "GM, Hotel Alpenblick · 142 rooms",
        },
        {
          text: "The Protel interfaces have worked flawlessly with our other systems.",
          who: "Marco Bühler",
          role: "Director, Grauhotel",
        },
        {
          text: "For our 2,400 units the voice agent is the first line.",
          who: "Jana Weiss",
          role: "COO, Verwaltung Süd",
        },
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      title_a: "Transparent.",
      title_b: "Per property.",
      sub: "No setup fee for the first 30 days. Annual billing available.",
      tiers: [
        {
          name: "Starter",
          price: "€ 490",
          per: "/ month / property",
          desc: "Entry tier for single properties — PMS integration, Core module, standard flows.",
          feats: ["1 PMS integration", "Core & Flows", "Email support"],
          cta: "Book a call",
        },
        {
          name: "Growth",
          price: "€ 1,290",
          per: "/ month / property",
          desc: "For properties that need Voice. Includes KOVA AI with 1,500 minutes.",
          feats: [
            "Everything in Starter",
            "KOVA AI · 1,500 min",
            "Automation Studio",
            "Priority support",
          ],
          cta: "Book a call",
          featured: true,
        },
        {
          name: "Enterprise",
          price: "Custom",
          per: "Chains & portfolios",
          desc: "Multi-property, SSO, custom integrations, SLA.",
          feats: [
            "Unlimited properties",
            "SSO & roles",
            "Custom integrations",
            "Dedicated CSM",
          ],
          cta: "Talk to sales",
        },
      ],
    },
    finalcta: {
      eyebrow: "Get started",
      title_a: "30 minutes.",
      title_b: "No pitch.",
      title_c: "Just your stack.",
      sub: "We'll look at your current setup, walk you through two or three automations, and tell you honestly whether Stratex-AI is a fit.",
      cta: "Book a call",
      cta2: "demo@stratex-ai.com",
    },
    footer: {
      tagline: "SaaS & automation for hospitality.",
      cols: [
        { title: "Product", items: ["Core", "KOVA AI", "Automation Studio", "Insights"] },
        { title: "Industries", items: ["Hospitality", "Property Mgmt", "F&B"] },
        { title: "Company", items: ["About", "Customers", "Careers", "Blog"] },
        { title: "Legal", items: ["Imprint", "Privacy", "Terms", "DPA"] },
      ],
      copy: "© 2026 Stratex-AI GmbH · Zürich · Vienna · Munich",
    },
  },
};

export type PmsEntry = { name: string; cat: string; logo?: string };

// PMS integrations list (shared across languages — descriptors are product names).
// Split into two marquee rows (11 each) in Integrations.tsx.
// Logos live in /public/logos/ (served from site root as /logos/*).
export const PMS_LIST: PmsEntry[] = [
  // Row A — PMS-heavy + booking/channel
  { name: "Protel", cat: "PMS", logo: "/logos/protel.svg" },
  { name: "Apaleo", cat: "PMS", logo: "/logos/apaleo.png" },
  { name: "Casablanca", cat: "PMS", logo: "/logos/casablanca.svg" },
  { name: "Mews", cat: "PMS", logo: "/logos/mews.svg" },
  { name: "Oracle Opera", cat: "PMS", logo: "/logos/oracle-opera.svg" },
  { name: "StayNTouch", cat: "PMS", logo: "/logos/stayntouch.svg" },
  { name: "RoomRaccoon", cat: "PMS", logo: "/logos/roomraccoon.svg" },
  { name: "HappyHotel", cat: "PMS", logo: "/logos/happyhotel.svg" },
  { name: "SmartHost", cat: "PMS", logo: "/logos/smarthost.svg" },
  { name: "SiteMinder", cat: "Channel", logo: "/logos/siteminder.png" },
  { name: "Dirs21", cat: "Booking Engine", logo: "/logos/dirs21.png" },
  // Row B — revenue, guest journey, POS, finance, voice, CRM
  { name: "IDeaS", cat: "Revenue Mgmt", logo: "/logos/ideas.jpg" },
  { name: "RoomPriceGenie", cat: "Revenue Mgmt", logo: "/logos/roompricegenie.png" },
  { name: "Straiv", cat: "Guest Journey", logo: "/logos/straiv.png" },
  { name: "Guestnet", cat: "Guest Engagement", logo: "/logos/guestnet.png" },
  { name: "Incert", cat: "Vouchers", logo: "/logos/incert.png" },
  { name: "Lightspeed", cat: "POS", logo: "/logos/lightspeed.png" },
  { name: "Gastronovi", cat: "POS", logo: "/logos/gastronovi.png" },
  { name: "DATEV", cat: "Finance", logo: "/logos/datev.svg" },
  { name: "Twilio", cat: "Telephony", logo: "/logos/twilio.png" },
  { name: "Fonio", cat: "Voice", logo: "/logos/fonio.png" },
  { name: "Salesforce", cat: "CRM", logo: "/logos/salesforce.png" },
];

export const CUSTOMER_LOGOS: string[] = [
  "Alpenblick Hotels",
  "Grauhotel",
  "Verwaltung Süd",
  "Café Ravello",
  "Gasthof Edelweiss",
  "Hotel zur Post",
  "Hotel Adler",
  "Trattoria Milano",
];
